import React, { useRef, useEffect, useState, useCallback } from 'react';

import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useProfile } from 'auth/Auth';
import { useTimer, blobToFile, formatTime, useCountDown } from 'common/recorder/Util';
import 'common/recorder/audio-recorder/AudioRecorder.scss';
import { ButtonVariant } from 'core/util/Bootstrap';

enum AudioRecordState {
    INITIAL,
    RECORDING_COUNTDOWN,
    RECORDING,
    RECORDING_PAUSED,
    PREVIEW,
    CONFIRM_RETAKE,
    ERROR,
}

interface AudioProps {
    onRecord?(file: File): any;
    onClose(): void;
    maxTimeLimit?: number;
    showCounterBeforeRecord?: boolean;
    countdownTimer?: number;
}

export function AudioRecorder(props: AudioProps): any {
    const { maxTimeLimit, showCounterBeforeRecord, countdownTimer } = props;
    const recorderRef = useRef<MediaRecorder>();
    const recordedChunks = useRef<any>([]);
    const [recordingState, setRecordingState] = useState<AudioRecordState>(AudioRecordState.INITIAL);
    const streamRef = useRef<MediaStream>();
    const { timer, handleStart, handlePause, handleResume, handleReset } = useTimer();
    const [blobURL, setBlobURL] = useState('');
    const { t } = useTranslation(['common/audio-recorder']);
    const [{ profile }] = useProfile();
    const { countDown, startCountDown, cancelCountdown } = useCountDown(countdownTimer);
    const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);

    const startRecording = useCallback(() => {
        recordedChunks.current = [];
        recorderRef.current!.start(10);
        handleStart();
        setRecordingState(AudioRecordState.RECORDING);
    }, [handleStart]);

    useEffect(() => {
        if (countDown === 0) {
            startRecording();
        }
    }, [countDown, startRecording]);

    const stopRecording = useCallback(() => {
        recorderRef.current!.stop();
        handleReset();
        processRecording();
    }, [handleReset]);

    useEffect(() => {
        async function init(): Promise<any> {
            if (navigator.mediaDevices) {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                    });

                    streamRef.current = stream;
                    recorderRef.current = new MediaRecorder(stream);

                    recorderRef.current.ondataavailable = (e: BlobEvent): void => {
                        if (e.data && e.data.size > 0) {
                            recordedChunks.current.push(e.data);
                        }
                    };
                } catch (e) {
                    setRecordingState(AudioRecordState.ERROR);
                }
            } else {
                setRecordingState(AudioRecordState.ERROR);
            }
        }
        init();
        return (): void => {
            // eslint-disable-next-line no-unused-expressions
            streamRef.current?.getTracks().forEach((track) => {
                track.stop();
            });
        };
    }, []);

    useEffect(() => {
        if (maxTimeLimit) {
            if (timer > maxTimeLimit) {
                setRecordingState(AudioRecordState.PREVIEW);
                stopRecording();
            }
        }
    }, [timer, handleReset, maxTimeLimit, stopRecording]);

    function processRecording(): void {
        const blob = new Blob(recordedChunks.current, {
            type: recordedChunks.current[0].type,
        });
        // generate video url from blob
        const audioURL = window.URL.createObjectURL(blob);

        setBlobURL(audioURL);
        setRecordingState(AudioRecordState.PREVIEW);
    }

    function handleFinishRecord(): any {
        const blob = new Blob(recordedChunks.current, {
            type: recordedChunks.current[0].type,
        });
        // generate video url from blob
        const fileName = `${profile?.tenant?.agencyID}-${profile?.tenant?.id}-${
            profile!.user.id
        }_Audio_Recording_${new Date().getTime()}.mp3`;

        const file = blobToFile(blob, fileName);

        if (props.onRecord) {
            props.onRecord(file);
        }
    }

    function handleRetake(): void {
        handleReset();
        setRecordingState(AudioRecordState.INITIAL);
    }

    function pauseRecording(): void {
        recorderRef.current!.pause();
        handlePause();
        setRecordingState(AudioRecordState.RECORDING_PAUSED);
    }

    function resumeRecording(): void {
        recorderRef.current!.resume();
        handleResume();
        setRecordingState(AudioRecordState.RECORDING);
    }

    function cancelRetake(): void {
        setRecordingState(AudioRecordState.PREVIEW);
    }

    function startRecordingCountDown(): void {
        setRecordingState(AudioRecordState.RECORDING_COUNTDOWN);
        startCountDown();
    }

    function handleCancelCountDown(): void {
        cancelCountdown();
        setRecordingState(AudioRecordState.INITIAL);
    }

    function renderRecorderActionsBasedOnStatus(): any {
        if (recordingState === AudioRecordState.ERROR) {
            return null;
        }

        if (AudioRecordState.RECORDING_COUNTDOWN === recordingState) {
            return (
                <button className="action-button-outlined" type="button" onClick={handleCancelCountDown}>
                    {t('CANCEL')}
                </button>
            );
        }

        if (recordingState === AudioRecordState.RECORDING_PAUSED) {
            return (
                <>
                    <button type="button" onClick={resumeRecording}>
                        <i className="icon-play" />
                        <div>{t('CONTINUE')}</div>
                    </button>
                    <button type="button" onClick={stopRecording}>
                        <i className="icon-stop" />
                        <div>{t('STOP')}</div>
                    </button>
                </>
            );
        }

        if (recordingState === AudioRecordState.RECORDING) {
            return (
                <>
                    <button type="button" onClick={pauseRecording}>
                        <i className="icon-pause" />
                        <div>{t('PAUSE')}</div>
                    </button>
                    <button type="button" onClick={stopRecording}>
                        <i className="icon-stop" />
                        <div>{t('STOP')}</div>
                    </button>
                </>
            );
        }

        if (recordingState === AudioRecordState.PREVIEW) {
            return (
                <>
                    <button
                        className="action-button-outlined"
                        type="button"
                        onClick={(): void => setRecordingState(AudioRecordState.CONFIRM_RETAKE)}
                    >
                        {t('RETAKE_RECORDING')}
                    </button>
                    <button className="action-button-filled" type="button" onClick={handleFinishRecord}>
                        {t('USE_THIS_RECORDING')}
                    </button>
                </>
            );
        }

        if (recordingState === AudioRecordState.CONFIRM_RETAKE) {
            return (
                <div className="retake-confirmation">
                    <div className="retake-confirm-msg">{t('CONFIRM_RETAKE_RECORDING_MESSAGE')}</div>
                    <button type="button" className="action-button-outlined" onClick={cancelRetake} aria-hidden="true">
                        {t('CANCEL')}
                    </button>
                    <button type="button" className="action-button-filled" onClick={handleRetake} aria-hidden="true">
                        {t('RETAKE')}
                    </button>
                </div>
            );
        }

        return (
            <>
                <button
                    type="button"
                    className="record-button action-button"
                    onClick={showCounterBeforeRecord ? startRecordingCountDown : startRecording}
                    aria-label={t('START_RECORDING')}
                >
                    <div className="record-icon" />
                </button>
            </>
        );
    }

    function renderRecordBody(): any {
        if (recordingState === AudioRecordState.INITIAL) {
            return t('BEGIN_RECORDING_MESSAGE');
        }
        if (recordingState === AudioRecordState.ERROR) {
            return t('ERROR_MESSAGE');
        }

        if (recordingState === AudioRecordState.RECORDING_COUNTDOWN) {
            return <p className="audio-record-countdown">{t('COUNTDOWN_MESSAGE', { countdown: countDown })}</p>;
        }

        if (recordingState === AudioRecordState.RECORDING || recordingState === AudioRecordState.RECORDING_PAUSED) {
            return (
                <p className="audio-record-timer">
                    {formatTime(timer)} {renderMaxTimeLimit()}
                </p>
            );
        }
        if (recordingState === AudioRecordState.PREVIEW || recordingState === AudioRecordState.CONFIRM_RETAKE) {
            return (
                <audio controls src={blobURL} autoPlay>
                    <track kind="captions" />
                </audio>
            );
        }

        return null;
    }

    function renderMaxTimeLimit(): any {
        if (maxTimeLimit) {
            return `/ ${formatTime(maxTimeLimit - timer)}`;
        }
        return '';
    }

    function handleCloseIcon(): any {
        if (recordingState === AudioRecordState.INITIAL) {
            return props?.onClose();
        }
        return setShowCancelConfirmationModal(true);
    }

    return (
        <div className="r4e-audio-recorder">
            <div className="record-container">
                <div className="title">
                    <h4>{t('RECORD_AUDIO')}</h4>
                    <button type="button" onClick={handleCloseIcon} className="close-button" aria-hidden="true">
                        <i className="icon-times-circle" />
                    </button>
                </div>
                <div className="record-body">{renderRecordBody()}</div>
                <div className="record-actions">{renderRecorderActionsBasedOnStatus()}</div>
            </div>
            <Modal show={showCancelConfirmationModal} centered>
                <Modal.Header onHide={(): void => setShowCancelConfirmationModal(false)} closeButton>
                    <Modal.Title>{t('CANCEL_MODAL_TITLE')}</Modal.Title>
                </Modal.Header>
                <Modal.Body bsPrefix="modal-body r4e-audio-recorder-cancel-modal-body">
                    <p>{t('CANCEL_MODAL_MESSAGE')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={(): void => setShowCancelConfirmationModal(false)} variant={ButtonVariant.Primary}>
                        {t('NO')}
                    </Button>
                    <Button onClick={props?.onClose} variant={ButtonVariant.Secondary}>
                        {t('CANCEL_MODAL_CONFIRM_BUTTON')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
